/**
 * Re-render the cart drawer in place instead of rebuilding it.
 *
 * Every cart change used to swap the drawer's innerHTML, which throws away every
 * element in it. For a moment after each quantity change the customer saw a
 * half-drawn drawer: product thumbnails blank while their lazy images loaded
 * again, the discount bar emptied to 0% and refilled, the cross-sell slider
 * hidden until Swiper initialised it again (the block collapses to its heading
 * meanwhile), and the item list scrolled back to the top.
 *
 * morphChildren() walks the fresh markup against the live drawer and only
 * touches what differs, so unchanged images, the slider and the scroll position
 * stay as they are, and the bar animates from its old width to the new one.
 *
 * Per element, first rule that applies:
 * - `data-morph-signature` (a hash of the block's server markup): left alone
 *   while the signature is the same, replaced whole otherwise. For blocks whose
 *   DOM a script rewrites, like the cross-sell slider.
 * - a custom element not listed in CART_DRAWER_MORPH_INTO: kept when identical,
 *   replaced whole otherwise, so its setup runs again exactly as it did with
 *   the innerHTML swap.
 * - anything else: attributes synced, then its children morphed.
 * Cart lines are matched by `data-line-id`, so a gift added at the top of the
 * list does not morph every row into its neighbour.
 */
const CART_DRAWER_MORPH_INTO = new Set(["cart-drawer-items", "discount-progress-bar"]);

function morphChildren(live, next) {
  const keyed = new Map();
  live.querySelectorAll(":scope > [data-line-id]").forEach((element) => {
    keyed.set(element.dataset.lineId, element);
  });
  const nextKeys = new Set(
    Array.from(next.querySelectorAll(":scope > [data-line-id]"), (element) => element.dataset.lineId),
  );

  let cursor = live.firstChild;
  Array.from(next.childNodes).forEach((nextChild) => {
    const key = nextChild.dataset?.lineId;
    let match = null;
    if (key !== undefined) {
      match = keyed.get(key) || null;
      keyed.delete(key);
    } else if (cursor && isSameMorphKind(cursor, nextChild)) {
      match = cursor;
    }

    if (!match) {
      live.insertBefore(nextChild, cursor);
      return;
    }
    // A line removed above this one (and the whitespace around it) goes now,
    // so this row stays where it is. Moving it would reconnect every custom
    // element in it and drop focus.
    while (cursor && cursor !== match && isDisposableMorphNode(cursor, nextKeys)) {
      const following = cursor.nextSibling;
      cursor.remove();
      cursor = following;
    }
    if (match === cursor) {
      cursor = cursor.nextSibling;
    } else {
      live.insertBefore(match, cursor);
    }
    morphNode(match, nextChild);
  });

  while (cursor) {
    const following = cursor.nextSibling;
    cursor.remove();
    cursor = following;
  }
}

function isSameMorphKind(live, next) {
  return (
    live.nodeType === next.nodeType &&
    live.nodeName === next.nodeName &&
    live.dataset?.lineId === next.dataset?.lineId
  );
}

/** Text, or a cart line the new markup no longer has: nothing will match it. */
function isDisposableMorphNode(node, nextKeys) {
  if (node.nodeType !== Node.ELEMENT_NODE) return true;
  const key = node.dataset.lineId;
  return key !== undefined && !nextKeys.has(key);
}

function morphNode(live, next) {
  if (live.nodeType !== Node.ELEMENT_NODE) {
    if (live.nodeValue !== next.nodeValue) live.nodeValue = next.nodeValue;
    return;
  }

  if (next.hasAttribute("data-morph-signature")) {
    if (
      live.getAttribute("data-morph-signature") !==
      next.getAttribute("data-morph-signature")
    ) {
      live.replaceWith(next);
    }
    return;
  }

  if (live.isEqualNode(next)) return;

  // A <template>'s markup lives in .content, which neither isEqualNode nor
  // childNodes look at.
  if (
    live.localName === "template" ||
    (live.localName.includes("-") && !CART_DRAWER_MORPH_INTO.has(live.localName))
  ) {
    live.replaceWith(next);
    return;
  }

  const valueChanged = live.getAttribute("value") !== next.getAttribute("value");
  const checkedChanged = live.hasAttribute("checked") !== next.hasAttribute("checked");

  Array.from(live.attributes).forEach((attribute) => {
    if (!next.hasAttributeNS(attribute.namespaceURI, attribute.localName)) {
      live.removeAttributeNS(attribute.namespaceURI, attribute.localName);
    }
  });
  Array.from(next.attributes).forEach((attribute) => {
    if (live.getAttributeNS(attribute.namespaceURI, attribute.localName) !== attribute.value) {
      live.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
    }
  });

  // Once a field has been touched its value no longer follows the attribute,
  // so carry a new server value across by hand. A value the server did not
  // change (a half-typed entry) is left alone.
  if (live.localName === "input") {
    if (valueChanged) live.value = next.getAttribute("value") ?? "";
    if (checkedChanged) live.checked = next.hasAttribute("checked");
  }

  morphChildren(live, next);
}

/**
 * Whether markup a cart request returned for the drawer is this theme's.
 *
 * Morphing anything else in strips the drawer of its lines and its close
 * button, and `this.closeButton().focus()` then threw on every cart update
 * (seen in Clarity on mobile Wonder sessions). Two ways to get such markup:
 * a section that failed to render comes back null, and during the theme A/B
 * test (a Shopify rollout against a Dawn-based theme) a request that reaches
 * the server without the visitor's theme assignment is answered by the other
 * theme, whose cart-drawer section has a #CartDrawer and .drawer__inner too.
 */
function isOwnCartDrawerMarkup(element) {
  return Boolean(element?.querySelector(".wt-cart__drawer__close"));
}

function reportRejectedCartDrawerMarkup() {
  console.warn("[cart-drawer] the cart answered with markup that is not this drawer's; kept the drawer as drawn");
  window.clarity?.("event", "cart_drawer_markup_rejected");
}

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
    this.openerFromTrigger = false;
    // Last focus outside the drawer: Add to cart, quick add and cross-sell open
    // it from code, without a trigger click to remember.
    this.lastOutsideFocus = null;
    this.keyboardInput = false;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    // A closed drawer is only moved off screen, so keep Tab out of it
    if (this.drawer) this.drawer.inert = true;
    document.addEventListener("focusin", (e) => {
      if (!this.contains(e.target)) this.lastOutsideFocus = e.target;
    });
    document.addEventListener("keydown", () => (this.keyboardInput = true), true);
    document.addEventListener("pointerdown", () => (this.keyboardInput = false), true);
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton()?.focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
    this.openerFromTrigger = true;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing. The page did not
    // move while the drawer was open, so do not scroll to the opener.
    requestAnimationFrame(() => {
      el.focus?.({ preventScroll: true });
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    // Only after a mouse or touch: a keyboard user needs to see where focus went
    if (this.keyboardInput) return;
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
      if (this.drawer) this.drawer.inert = true;

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      if (!this.openerFromTrigger) this.openerEl = this.lastOutsideFocus;
      this.openerFromTrigger = false;
      this.setAttribute("open", "");
      this.isOpen = true;
      if (this.drawer) this.drawer.inert = false;
      this.closeButton()?.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );

    // Checkout is a plain form post, so nothing changes on the page until the
    // checkout loads: show the button's spinner meanwhile. The button is not
    // disabled, or its name=checkout would drop out of the post.
    this.addEventListener("submit", (e) => {
      const button = e.submitter;
      if (!button || button.name !== "checkout") return;
      setTimeout(() => {
        if (!e.defaultPrevented) this.setCheckoutLoading(button, true);
      });
    });
    // Back from checkout brings this page back from the back/forward cache
    window.addEventListener("pageshow", (e) => {
      if (!e.persisted) return;
      this.querySelectorAll('[name="checkout"].loading').forEach((button) =>
        this.setCheckoutLoading(button, false),
      );
    });
  }

  setCheckoutLoading(button, loading) {
    button.classList.toggle("loading", loading);
    if (loading) button.setAttribute("aria-busy", "true");
    else button.removeAttribute("aria-busy");
    button
      .querySelector(".loading-overlay__spinner")
      ?.classList.toggle("hidden", !loading);
  }

  /**
   * @param {object} parsedState - `{ sections }` as the Section Rendering API returns them
   * @param {boolean} [isClosedCart=true] - open the drawer afterwards
   * @param {object} [options]
   * @param {boolean} [options.current] - the markup is of the cart as it stands
   *   now: this theme's own redraws pass it. Others are held while a quantity
   *   change is queued or in flight. The AOV.ai bundle app redraws the drawer
   *   from sections it fetched itself ~1.5s after every cart change; markup
   *   fetched before a change still running would roll its line back. One fresh
   *   redraw follows once the queue has drained (assets/cart.js).
   * @param {boolean} [options.retried] - the markup comes from refreshCartDrawer(),
   *   so markup that is not this drawer's is not fetched again.
   */
  renderContents(parsedState, isClosedCart = true, { current = false, retried = false } = {}) {
    const intents = window.cartQuantityIntent;
    if (!current && !isClosedCart && intents?.isBusy()) {
      intents.refreshDrawerWhenIdle = true;
      return;
    }

    const updates = this.getSectionsToRender().map((section) => ({
      live: section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id),
      next: this.getSectionElement(parsedState.sections?.[section.id], section.selector),
    }));
    if (!isOwnCartDrawerMarkup(updates[0].next)) {
      // Leave the drawer as it is drawn. A redraw from a fresh fetch is tried
      // once; a refresh that gets the same answer stops here.
      reportRejectedCartDrawerMarkup();
      if (!retried) this.refreshCartDrawer(null, { open: isClosedCart });
      return;
    }
    updates.forEach(({ live, next }) => {
      if (live && next) morphChildren(live, next);
    });

    // Same task as the swap, so a quantity being edited does not repaint with
    // the cart's slightly stale value. See window.cartQuantityIntent.
    window.cartQuantityIntent?.restore();

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton()?.focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return this.getSectionElement(html, selector).innerHTML;
  }

  getSectionElement(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  /**
   * @param {Event} [e]
   * @param {object} [options]
   * @param {boolean} [options.open] - open the drawer once it is redrawn
   */
  refreshCartDrawer(e, { open = false } = {}) {
    const sectionsToRender = this.getSectionsToRender();
    // Returned so callers (e.g. cart-free-gifts.js) can await the re-render.
    return fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, open, { current: true, retried: true });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const source = html.querySelector("cart-drawer-items");
        if (!isOwnCartDrawerMarkup(html)) {
          reportRejectedCartDrawerMarkup();
        } else if (source) {
          morphChildren(this, source);
          window.cartQuantityIntent?.restore();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderSection(target, html, selector) {
    const source = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    const isDrawer = target.matches(".drawer__inner");
    if (source && (!isDrawer || isOwnCartDrawerMarkup(source))) {
      morphChildren(target, source);
    } else if (isDrawer) {
      // Blanking the drawer here (what a missing section used to do) left it
      // without its close button. Keep it and draw it from a fresh fetch.
      reportRejectedCartDrawerMarkup();
      document.querySelector("cart-drawer")?.refreshCartDrawer?.();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
